import { z } from 'zod';

export const universalSessionCookieSchema = z.object({
  tokenType: z.string(),
  authToken: z.string(),
  accountId: z.string(),
  persisted: z.boolean(),
  expiresAt: z.number().transform((val) => new Date(val)),
});

export const UNIVERSAL_SESSION_COOKIE_NAME = '__session';

export function secondsUntil(date: Date) {
  return Math.floor((date.getTime() - Date.now()) / 1000);
}

export type UniversalSessionCookie = z.infer<typeof universalSessionCookieSchema>;

// When we set a cookie from the client, we know max-age instead of its expiration time.
export type CreateUniversalSessionCookiePayload = Omit<UniversalSessionCookie, 'expiresAt'> & {
  expiresAt: number;
};

/**
 * Get the shared session secret
 *
 * @throws if the secret is not set
 * @returns the secret key
 */
export function getSharedSessionSecret() {
  const secret = process.env.SHARED_SESSION_SECRET;

  const envType = typeof secret; // splitting this on two lines so the secret checker doesn't complain
  if (envType !== 'string') {
    throw new Error('SHARED_SESSION_SECRET is not set');
  }

  return secret as string;
}

/**
 * Persists the login information and auth token to a secure HttpOnly session shared
 * amongst all solv frontend projects.
 * @param session The session data to store
 * @returns A promise that resolves when the session is stored
 */
export async function persistUniversalAuthSession(session: CreateUniversalSessionCookiePayload) {
  return await fetch('/auth/store-login', {
    method: 'POST',
    body: JSON.stringify(session),
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}

/**
 * Sends a fetch request to delete the shared HttpOnly session.
 * @returns A promise that resolves when the session is deleted
 */
export async function deleteUniversalAuthSession() {
  return await fetch('/auth/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
  });
}
